import fabric from '../../common/fabric';

import variables from '../../common/variables';
import * as canvasActions from './canvasActions';
import * as objectActions from './objectActions';
import apiActions from '../../common/actions/apiActions';
import { select } from './selectionActions';
import { migrateJSON } from '../../common/migrations';
import utils from '../../common/utils';
import { isObjectOfType } from '../../common/objects';
import smalltalk from '../../vendor/smalltalk';
import SparkMD5 from 'spark-md5';

import {
  createSymbolMutation,
  updateSymbolMutation,
} from '../graphql/mutations';

/**
 * Create a new symbol
 *
 * We need to dispatch various object & api actions to perform the whole flow
 *
 * @param name
 * @param tags
 * @param teamId
 * @param activeObject
 * @returns {function(*=): Promise}
 */
export const createSymbol = (name, tags, teamId, activeObject) => (
  dispatch,
  getState,
) =>
  new Promise((resolve, reject) => {
    if (getState().context.isGuest) {
      smalltalk.alert(
        'Sorry',
        `Guest can't creat Smart Block. Check with the workspace admin to get access.`,
      );
      return;
    }
    dispatch(objectActions.prepareForSymbol(activeObject)) // convert to group
      .then(symbolGroup => {
        // add unique identifier to every group child - we will need this for
        // symbol overrides
        symbolGroup.forEachObject(utils.addLudusIds);

        dispatch({ type: 'SYMBOL_SAVE_STATUS_CHANGED', status: 1 });
        createSymbolMutation(
          name,
          JSON.stringify(symbolGroup.toJSON(variables.propsToSave)),
          tags,
          teamId,
        )
          .then(symbolData => {
            dispatch(
              objectActions.replaceGroupBySymbol(
                symbolGroup,
                symbolData.data.createSymbol.symbol,
              ),
            );
            dispatch({ type: 'SYMBOL_SAVE_STATUS_CHANGED', status: 0 });
          })
          .catch(() => {
            dispatch({ type: 'SYMBOL_SAVE_STATUS_CHANGED', status: 0 });
            reject();
          });
      })
      .then(resolve)
      .catch(reject);
  });

/**
 * Update a symbol
 *
 * @param id
 * @param name
 * @param tags
 * @param teamId
 * @param content
 * @returns {function(*=): Promise}
 */
export const updateSymbol = (id, name, tags, teamId, content) => () =>
  new Promise((resolve, reject) => {
    updateSymbolMutation(id, name, content, tags, teamId)
      .then(() => {
        resolve();
      })
      .catch(() => {
        reject();
      });
  });

/**
 * Flatten the symbol to an active selection
 *
 * @param object
 */
export const unlink = object => (dispatch, getState, fabricCanvas) => {
  if (isObjectOfType(object, 'symbol')) {
    const activeSelection = object.toActiveSelection();
    if (activeSelection.getObjects().length === 1) {
      // we need this to dsiplay the properties of the real object
      // and not from a "selection" of 1 object
      fabricCanvas.setActiveObject(activeSelection._objects[0]);
    } else if (activeSelection.getObjects().length > 1) {
      // force ludus to null, or objects.jsx will think it's still a symbol and display wrong buttons in right panel
      activeSelection.ludus = null;
    }
    const activeObject = fabricCanvas.getActiveObject();
    dispatch(select(activeObject));
    objectActions.dispatchModified(activeObject)(dispatch, getState);
    utils.removeLudusIds(activeObject);
    fabricCanvas.requestRenderAll();
  }
};

export const renameSymbol = name => ({
  type: 'SYMBOL_RENAME',
  name,
});

/**
 * Migrate old symbols (saved as plain object or active selection)
 *
 * Almost the same as createSymbol - should be deleted once all symbols are migrated
 *
 * @param name
 * @param oldSymbolContent
 * @param symbolId
 * @returns {function(*=): Promise}
 */
export const convertToSymbol = (name, oldSymbolContent, symbolId) => dispatch =>
  new Promise((resolve, reject) => {
    const migratedSymbolContent = migrateJSON(oldSymbolContent);
    fabric.util.enlivenObjects([migratedSymbolContent], items => {
      const oldSymbol = items[0];

      // Fix for old "active groups" in fabric 1.x
      if (!oldSymbol.ludus && oldSymbol.type === 'group') {
        oldSymbol.ludus = { type: 'group' };
      }

      dispatch(objectActions.prepareForSymbol(oldSymbol)) // convert to group
        .then(symbolGroup => {
          // add unique identifier to every group child - we will need this for
          // symbol overrides
          symbolGroup.forEachObject(utils.addLudusIds);

          dispatch({ type: 'SYMBOL_SAVE_STATUS_CHANGED', status: 1 });
          return updateSymbolMutation(
            symbolId,
            name,
            JSON.stringify(symbolGroup.toJSON(variables.propsToSave)),
          )
            .then(symbolData => {
              dispatch({ type: 'SYMBOL_SAVE_STATUS_CHANGED', status: 0 });
              dispatch(
                objectActions.replaceGroupBySymbol(
                  symbolGroup,
                  symbolData.data.updateSymbol.symbol,
                ),
              );
            })
            .catch(() => {
              dispatch({ type: 'SYMBOL_SAVE_STATUS_CHANGED', status: 0 });
              smalltalk.alert(
                'Oops',
                'Your symbol could not be added. Please try again.',
              );
            });
        })
        .then(resolve)
        .catch(reject);
    });
  });

/**
 * Add an instance-level override to a symbol child
 *
 * @param symbol
 * @param childId (as in child.ludus.id)
 * @param overridenProp
 * @param overriddenValue
 * @returns {function(*)}
 */
export const overrideSymbol = (
  symbol,
  childId,
  overridenProp,
  overriddenValue,
) => (dispatch, getState) => {
  symbol.addOverride(childId, overridenProp, overriddenValue);
  objectActions.dispatchModified(symbol)(dispatch, getState);
};

/**
 * Enter "symbol edition mode"
 *
 * @param symbolId
 * @param undoOrRedo: used in undo/redo actions, see comment below
 * @returns {*}
 */
export const startSymbolEditionById = (symbolId, undoOrRedo = false) => (
  dispatch,
  getState,
  fabricCanvas,
) =>
  new Promise((resolve, reject) => {
    // When startSymbolEditionById() is called from the undo / redo action creators, we basically
    // don't need to do anything, except dispatching SYMBOL_EDITION_START_UNDO_REDO so that
    // saveReducer/symbolSaveReducer can keep track of the current edition mode.
    // We dispatch SYMBOL_EDITION_START_UNDO_REDO instead of SYMBOL_EDITION_START to avoid creating
    // an extra entry in the undoable history.
    if (undoOrRedo) {
      dispatch({ type: 'SYMBOL_EDITION_START_UNDO_REDO' });
      resolve();
    } else {
      const state = getState();
      const isGuest = state.context.isGuest;
      if (isGuest) {
        smalltalk.alert(
          'Sorry',
          'Guests are not allowed to edit symbols. Please, request to be a regular member to the workspace admin.',
        );
        return;
      }
      dispatch(apiActions.getSymbol(symbolId))
        .then(symbolData => {
          // First, we need to enliven the group object returned in our API so that
          // we may use group features such as ungroupOnCanvas
          fabric.util.enlivenObjects(
            [JSON.parse(symbolData.content)],
            objects => {
              const symbolGroup = objects[0];

              symbolGroup.originX = 'left';
              symbolGroup.originY = 'top';
              symbolGroup.left = 0;
              symbolGroup.top = 0;

              // Call ungroup on canvas to transform the group objects coordinates
              // This is necessary to position the group objects on our canvas
              // when editing the symbol
              symbolGroup.ungroupOnCanvas();

              const firebaseSymbol =
                fabric.LudusSymbol.registered?.[symbolData.id];

              const symbolGroupObjects = symbolGroup
                .getObjects()
                .map(o => o.toJSON(variables.propsToSave));
              fabricCanvas.discardActiveObject();
              dispatch({
                type: 'SYMBOL_EDITION_START',
                symbolData: {
                  id: symbolData.id,
                  name: symbolData.name,
                  team: symbolData.team,
                },
                symbolContent: {
                  objects: symbolGroupObjects,
                  background: firebaseSymbol.content.background || '#FFF',
                },
                symbolDimensions: {
                  width: symbolGroup.width,
                  height: symbolGroup.height,
                },
              });
              resolve();
            },
          );
        })
        .catch(reject);
    }
  });

/**
 * Trigger group update in state - will trigger canvas update in turn
 *
 * @param width
 * @param height
 * @returns {{type: string, symbolDimensions: {width: *, height: *}}}
 */
export const updateSymbolDimensions = (width, height) => ({
  type: 'SYMBOL_EDITION_DIMENSIONS_UPDATE',
  symbolDimensions: { width, height },
});

/**
 * Leave "symbol edition mode"
 *
 * @param undoOrRedo: used in undo/redo actions, see comment below
 * @returns {function(*=, *, *=): Promise}
 */
export const endSymbolEdition = (undoOrRedo = false) => (
  dispatch,
  getState,
  canvas,
) =>
  new Promise((resolve, reject) => {
    // Clean canvas - especially useful to prevent issues when editing text
    dispatch(canvasActions.cleanCanvas());

    const presentation = getState().contentReducer.presentation;
    const symbolState = presentation.symbol;

    // The symbol objects are stored as plain objects in our state so the first step
    // is to enliven them so they can be added to a new group representing the symbol
    fabric.util.enlivenObjects(symbolState.content.objects, objects => {
      // Create a new group and let it choose its width & height, without taking into account
      // the dimensions chosen by the user
      utils.addLudusIds(objects);
      const newSymbolGroup = new fabric.Group(objects);

      // Now we need to calculate the difference between the "natural" width & height
      // and the "chosen" width & height
      // (i.e. the ones chosen by the user when editing the symbol)
      const { width, height } = symbolState.dimensions;
      const widthDiff = width - newSymbolGroup.width;
      const heightDiff = height - newSymbolGroup.height;
      // When we will override the natural group width & height, Fabric will re-center the
      // group objects for us - we don't want that so lets cancel this effect
      newSymbolGroup.forEachObject(o => {
        o.set('left', o.get('left') - widthDiff / 2 + newSymbolGroup.left);
        o.set('top', o.get('top') - heightDiff / 2 + newSymbolGroup.top);
      });

      newSymbolGroup.set('width', width);
      newSymbolGroup.set('height', height);
      newSymbolGroup.background = symbolState.content.background;

      // add unique identifier to every group child - we will need this for
      // symbol overrides
      newSymbolGroup.forEachObject(utils.addLudusIds);
      const symbolAsJSON = JSON.stringify(
        newSymbolGroup.toJSON(variables.propsToSave),
      );

      newSymbolGroup.set('hash', SparkMD5.hash(symbolAsJSON));
      const symbolWithHashAsJSON = JSON.stringify(
        newSymbolGroup.toJSON(variables.propsToSave),
      );
      const { id, name, tags } = symbolState;
      const teamId = window.team ? window.team.id : null;

      dispatch({ type: 'SYMBOL_SAVE_STATUS_CHANGED', status: 1 });

      updateSymbolMutation(id, name, symbolWithHashAsJSON, tags, teamId)
        .then(updatedSymbolData => {
          const symbol = updatedSymbolData.data.updateSymbol.symbol;
          dispatch({ type: 'SYMBOL_SAVE_STATUS_CHANGED', status: 0 });
          // We need to register the symbol name and content in our custom Fabric class
          fabric.LudusSymbol.register(
            symbol.id,
            symbol.name,
            JSON.parse(symbol.content),
          );
          // Our custom fabric class instances listen to this event to update themselves
          canvas.trigger('symbol:updated', { reference: symbol.id });

          // When endSymbolEdition() is called from the undo / redo action creators, we don't want
          // to dispatch the SYMBOL_EDITION_END action (as it would add an entry in the undoable
          // history). We dispatch SYMBOL_EDITION_END_UNDO_REDO instead, which won't be used in
          // canvasReducer, but can be used in contentReducer to keep
          // track of the current edition mode.
          const eventType = undoOrRedo
            ? 'SYMBOL_EDITION_END_UNDO_REDO'
            : 'SYMBOL_EDITION_END';
          dispatch({ type: eventType });
          const modifiedSlides = [];
          if (modifiedSlides.length) {
            dispatch({
              type: 'SYMBOLS_UPDATED',
              ids: modifiedSlides,
            });
          }
          dispatch({
            type: 'SYMBOL_UPDATED',
            id: symbol.id,
            content: JSON.parse(symbol.content),
          });
          resolve();
        })
        .catch(e => {
          dispatch({ type: 'SYMBOL_SAVE_STATUS_CHANGED', status: 0 });
          reject(e);
        });
    });
  });

export const cancelSymbolEdition = () => ({ type: 'SYMBOL_EDITION_END' });

/**
 * Register symbols in our fabric.LudusSymbol class
 * We also want to pass a reference to the startSymbolEditionById action creator in
 * our LudusSymbol class, so that instances can call it when appropriate
 *
 * @param symbolsData
 */
export const registerSymbols = symbolsData => dispatch =>
  new Promise(resolve => {
    fabric.LudusSymbol.registerEditionCallback(symbolId => {
      dispatch(startSymbolEditionById(symbolId));
    });

    symbolsData.forEach(symbol => {
      const symbolContent = migrateJSON(JSON.parse(symbol.content));
      if (symbol.version === 2) {
        fabric.LudusSymbol.register(symbol.id, symbol.name, symbolContent);
      }
    });

    resolve();
  });

/**
 * Unregister symbol & trigger canvas update so that existing instances on canvas
 * are flagged as broken
 *
 * @param symbolId
 */
export const unregisterSymbol = symbolId => (dispatch, getState, canvas) => {
  fabric.LudusSymbol.unregister(symbolId);
  canvas.trigger('symbol:deleted', { reference: symbolId });
};

/**
 * Update symbol thumbnail cache
 *
 * @param symbolId
 * @param thumbnailData
 */
export const updateSymbolThumbnail = (
  symbolId,
  width,
  height,
  thumbnailData,
) => dispatch => {
  dispatch({
    type: 'SYMBOL_THUMBNAIL_UPDATED',
    id: symbolId,
    width,
    height,
    data: thumbnailData,
  });
};
